import React from "react";
import { useAuth } from "../context/AuthContext";

import Header from "../components/Header";
import Footer from "../components/Footer";

import logo from "../assets/images/logo.png";
import heroBackground from "../assets/images/hero-background.png";

export default function Terms() {
  const { user } = useAuth();

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-[#050613] text-white bg-no-repeat bg-contain bg-center-top"
      style={{
        backgroundImage: `url(${heroBackground})`,
      }}
    >
      <div className="pointer-events-none absolute inset-0" />
      <Header user={user} logo={logo} />

      <main className="px-4 pt-10 pb-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-4xl animate-fadeInSoft" style={{ animationDelay: "80ms" }}>
            <div className="mb-8 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs tracking-widest text-white/80 backdrop-blur">
                + TERMS
              </span>
              <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-medium text-white/90">
                Terms Of Service
              </h1>
            </div>

            <div className="space-y-7 text-sm leading-relaxed text-white/80">
              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Introduction</h2>
                <p>
                  These Terms of Service (&quot;Terms&quot;) govern the contractual relationship between you
                  (&quot;User&quot;, &quot;Customer&quot;) and MyRDP (&quot;MyRDP&quot;, &quot;we&quot;, &quot;us&quot;,
                  &quot;our&quot;).
                </p>
                <p>
                  By registering for, accessing, or utilizing any of our RDP, VPS, or associated hosting services
                  (collectively, the &quot;Services&quot;), you acknowledge that you have read, understood, and agree to
                  be legally bound by these Terms.
                </p>
                <p>
                  If you do not accept these Terms in their entirety, you must immediately refrain from using our
                  Services.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Service Overview</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    MyRDP delivers virtualized remote desktop and server hosting solutions, providing allocated computing
                    resources such as CPU, RAM, storage, and bandwidth as specified at the time of order.
                  </li>
                  <li>All Services are provisioned electronically and activated following successful payment confirmation.</li>
                  <li>
                    To maintain service quality, security, and operational efficiency, MyRDP reserves the right to
                    modify or upgrade its infrastructure, including but not limited to hardware, network architecture,
                    virtualization technologies, and service locations.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Billing and Payment Terms</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    All Services are offered on a prepaid basis and must be paid in full prior to activation, renewal, or
                    reinstatement.
                  </li>
                  <li>
                    <span className="font-semibold text-white/90">Cryptocurrency only (current offering)</span>: At
                    present, MyRDP accepts payments exclusively via supported cryptocurrencies processed through an
                    authorized third-party payment provider. Traditional payment methods such as credit cards, debit
                    cards, or PayPal are not supported. Service fees are deducted from the User&apos;s prepaid account
                    balance following a successful cryptocurrency deposit.
                  </li>
                  <li>
                    Failure to maintain sufficient account balance may result in immediate suspension and eventual
                    termination or deletion of the Service without liability. Restoration of data or services is not
                    guaranteed.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Refund Policy</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>All transactions are deemed final. Refunds will not be issued except where mandated by applicable law.</li>
                  <li>
                    Any discretionary credits, partial refunds, or service extensions may be granted solely at the
                    discretion of MyRDP and shall not constitute an obligation.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Payment Disputes and Investigations</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Due to the irreversible nature of cryptocurrency transactions, chargebacks are not supported.</li>
                  <li>
                    In the event of a payment discrepancy, Users are required to contact MyRDP support prior to
                    initiating any dispute with the payment processor.
                  </li>
                  <li>
                    Non-cooperation, abuse of dispute mechanisms, or suspected fraudulent activity may result in immediate
                    service suspension or termination.
                  </li>
                  <li>
                    MyRDP reserves the right to disclose relevant account, service, and transaction data to payment
                    providers or competent authorities where necessary for dispute resolution, fraud prevention, or legal
                    compliance.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">User Obligations</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    Users are responsible for ensuring that their use of the Services complies with all applicable
                    local, national, and international laws and regulations.
                  </li>
                  <li>
                    Users must maintain the confidentiality and security of their account credentials and are fully
                    responsible for all activities conducted under their account.
                  </li>
                  <li>
                    Users are solely responsible for implementing and maintaining independent data backups. MyRDP assumes
                    no responsibility for data loss under any circumstances.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Prohibited Use</h2>
                <p>The following activities are strictly prohibited on all MyRDP Services:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Initiating or participating in Distributed Denial-of-Service (DDoS) attacks or network stress testing</li>
                  <li>Distribution of unsolicited communications, including spam or bulk messaging</li>
                  <li>
                    Deployment or operation of botnets, malware, phishing systems, or unauthorized access tools
                  </li>
                  <li>
                    Cryptocurrency mining or any sustained excessive resource consumption affecting system stability
                  </li>
                  <li>Unauthorized port scanning, vulnerability assessments, or reconnaissance activities</li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Suspension and Termination Rights</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    MyRDP reserves the right to suspend, restrict, or terminate Services at its sole discretion, with or
                    without prior notice, in cases of policy violations, activities posing risk to infrastructure or
                    network integrity, or where required by law.
                  </li>
                  <li>No refunds or compensation will be provided for Services terminated due to breaches of these Terms.</li>
                  <li>
                    MyRDP may permanently remove any data, virtual machines, or backups associated with prohibited or
                    abusive activities.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Limitation of Liability</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    To the fullest extent permitted by applicable law, MyRDP shall not be liable for any indirect,
                    incidental, consequential, or punitive damages, including but not limited to loss of data, revenue,
                    profits, or business opportunities.
                  </li>
                  <li>
                    In all cases, MyRDP&apos;s total aggregate liability shall not exceed the amount paid by the User for
                    the affected Service within the thirty (30) days preceding the event giving rise to the claim.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Service Availability and Credits</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    MyRDP targets an uptime level of 99.5%. However, availability may be impacted by scheduled
                    maintenance, upstream provider issues, mitigation of abuse, or events beyond reasonable control.
                  </li>
                  <li>
                    Any compensation for service interruptions shall be granted solely at MyRDP&apos;s discretion and, if
                    applicable, will be issued in the form of service credits or additional service time.
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Amendments to Terms</h2>
                <p>
                  MyRDP reserves the right to revise or update these Terms at any time without prior notice.
                </p>
                <p>
                  Continued use of the Services following the publication of updated Terms constitutes acceptance of such
                  changes.
                </p>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Support and Contact Information</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    For general inquiries or technical support, Users may contact MyRDP via the support ticket system or
                    by email at{" "}
                    <a href="mailto:support@myrdp.io" className="text-[#486FFF] underline underline-offset-2 hover:text-[#7c9cff]">
                      support@myrdp.io
                    </a>
                    .
                  </li>
                  <li>
                    For abuse-related reports, please contact{" "}
                    <a href="mailto:support@myrdp.io" className="text-[#486FFF] underline underline-offset-2 hover:text-[#7c9cff]">
                      support@myrdp.io
                    </a>
                    .
                  </li>
                </ul>
              </section>

              <section className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#486FFF]">Company Information</h2>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Company: MyRDP</li>
                  <li>
                    Email:{" "}
                    <a href="mailto:support@myrdp.io" className="text-[#486FFF] underline underline-offset-2 hover:text-[#7c9cff]">
                      support@myrdp.io
                    </a>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer user={user} />
    </div>
  );
}
